import React, { useMemo } from 'react';
import { Box, Text, VStack } from '@cad-arc';
import { AttachmentDto } from '@mark43/cad-api';
import { MergedAttachmentsGrid } from './merged-attachments-grid';
import { AttachmentSortOptionValue, AttachmentsPanelType } from './types';
import { useIntegratedEventMergedEventsQuery } from '../event-panel/event-state/use-integrated-event-merged-events-query';
import { groupMergedAttachmentsByEvent } from './helpers';
import { LoadingIndicator } from '~/common/components/loading-indicator';

interface MergedAttachmentsSectionProps {
    parentEventId: number;
    mergedAttachments: AttachmentDto[];
    entityType: AttachmentsPanelType;
    onOpenPreview?: (attachmentId: number) => void;
    sortOption: AttachmentSortOptionValue;
}

export function MergedAttachmentsSection({
    parentEventId,
    mergedAttachments,
    entityType,
    onOpenPreview,
    sortOption,
}: MergedAttachmentsSectionProps): React.ReactElement | null {
    const { data: mergedEventsData, isLoading } = useIntegratedEventMergedEventsQuery({
        eventId: parentEventId,
        enabled: mergedAttachments.length > 0,
    });

    const groupedAttachments = useMemo(() => {
        if (!mergedAttachments) return [];
        return groupMergedAttachmentsByEvent(mergedAttachments);
    }, [mergedAttachments]);

    // getting entityNumber from mergedEvents query as mergedAttachments only has the entityId
    const eventNumberMap = useMemo(
        () =>
            new Map(
                mergedEventsData?.mergedEvents?.map((event) => [
                    event.eventId,
                    event.eventNumber,
                ]) || []
            ),
        [mergedEventsData]
    );

    if (!mergedAttachments || mergedAttachments.length === 0) {
        return null;
    }

    return isLoading ? (
        <LoadingIndicator />
    ) : (
        <Box flexGrow={1} mt="var(--arc-space-2)">
            <Text variant="headingSm">Merged Attachments</Text>
            <VStack align="stretch">
                {groupedAttachments.map((group) => (
                    <MergedAttachmentsGrid
                        key={group.eventId}
                        eventId={group.eventId}
                        eventNumber={eventNumberMap.get(group.eventId) || ''}
                        attachments={group.attachments}
                        entityType={entityType}
                        onOpenPreview={onOpenPreview}
                        sortOption={sortOption}
                    />
                ))}
            </VStack>
        </Box>
    );
}
